import heic2any from 'heic2any'
import get from 'lodash/get'
import split from 'lodash/split'
import PropTypes from 'prop-types'
import * as React from 'react'
import DraggableFileUploadField from 'ui/molecules/DraggableFileUploadField'
import { sanitizeImage } from 'utils/attackPrevention/images'
import { downloadFromURL } from 'utils/downloadManager'
import { buildFullName } from 'utils/helpers'

import ZentakeFormContext from '../ZentakeFormContext'

function DraggableFileUploadQuestion({ question, isOfficeUse }) {
  const formContext = React.useContext(ZentakeFormContext)
  const readonly = formContext.isQuestionReadonly(question)
  const answers = formContext.getAnswers(question)
  const [errorLabel, setErrorLabel] = React.useState('')

  const isDirty = formContext.isQuestionDirty(question)
  const isValid = formContext.isQuestionValid(question)

  const files = React.useMemo(() => {
    if (!answers) return []
    return answers.flatMap((a) => {
      if (a.file) {
        return [
          {
            name: a.file.split('/files/private/files/')[1],
            url: a.file,
            status: 'done',
          },
        ]
      }
      return a.answer_files
    })
  }, [answers])

  const acceptedFileTypes = React.useMemo(() => {
    const fileExtensions = get(question, 'file_extensions', [])
    if (!fileExtensions.length) return undefined
    return fileExtensions.map((ext) => `.${ext}`).join(',')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?.file_extensions])

  async function processFile(file: File) {
    let _file = await sanitizeImage(file)

    const splittedFileName = _file.name.split('.')
    let fileExtension = splittedFileName[splittedFileName.length - 1]

    if (fileExtension.toLowerCase() === 'heic') {
      _file = await heic2any({
        blob: _file,
        toType: 'image/jpeg',
        quality: 0.5,
      })
      fileExtension = 'jpeg'
    }

    return new File([_file], _file.name, {
      type: _file.type,
    })
  }

  async function handleChange(files: File[]) {
    setErrorLabel('')
    if (files?.length) {
      const _files = await Promise.all(files.map(processFile))

      formContext.onAnswerChanged(question, _files)
    } else {
      formContext.onAnswerChanged(question, null)
    }
  }

  async function handleOnDrop(event: React.DragEvent<HTMLDivElement>) {
    // Validate file extension
    const files = event.dataTransfer.files

    // Check each file for valid extension
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      const splittedFileName = file.name.split('.')
      const fileExtension = splittedFileName[splittedFileName.length - 1]

      if (!question.file_extensions.includes(fileExtension)) {
        setErrorLabel(
          `Invalid file type. Please upload a file with the following extensions: ${question.file_extensions.join(
            ', '
          )}`
        )
        return
      }
    }
  }

  const hasError = isDirty && question.is_required && !isValid

  const handleDownloadFile = (file) => {
    const fileUrl = file.originFileObj.url
    const clientName =
      buildFullName(
        formContext?.client?.first_name,
        formContext?.client?.last_name
      ) || get(formContext, 'formName', '')
    const questionName = question?.text || split(fileUrl, '/').pop()
    const ext = split(fileUrl, '.').pop()
    const fileName = `${clientName} - ${questionName}.${ext}`
    downloadFromURL(fileUrl, fileName)
  }

  return (
    <DraggableFileUploadField
      semibold
      showUploadList
      accept={acceptedFileTypes}
      allowDownload={readonly}
      disabled={readonly}
      errorLabel={errorLabel || 'This field is required'}
      files={files}
      handleDownload={handleDownloadFile}
      hasError={!!errorLabel || hasError}
      id={question.id}
      isOfficeUse={isOfficeUse}
      isRequired={question.is_required}
      label={question.text}
      multiple={question.multi_file_upload}
      name={question.id}
      onChange={handleChange}
      onDrop={handleOnDrop}
    />
  )
}

DraggableFileUploadQuestion.propTypes = {
  question: PropTypes.object.isRequired,
}

export default React.memo(DraggableFileUploadQuestion)
